/**
 * Arreglo 1.4 (oct-2026) — Venta sin cita (walk-in): cliente opcional (buscar / crear), servicio del menú o
 * concepto libre + precio → crea la cita walk-in y abre la MISMA pantalla de cobro (SaleSheet: artículos,
 * descuento, propina, QR/enlace, efectivo; Tap to Pay oculto). Recibo, Mi Dinero e historial salen del riel real.
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, UserPlus, X, Scissors, Receipt, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { bookingApi } from '@/lib/bookingApi';
import { api } from '@/lib/api';
import { clientsApi } from '@/lib/clientsApi';
import { SaleSheet } from '@/components/Bookings/SaleSheet';
import { DuplicateAskDialog, isNameDuplicateError } from '@/components/Clients/DuplicateAskDialog';
import { tr } from '@/contexts/LanguageContext';
import { fmtMoney } from '@/utils/money';

const money = (v) => fmtMoney(v);

export const WalkInSalePanel = ({ user, presetClient, onReset }) => {
  const [client, setClient] = useState(presetClient || null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [newMode, setNewMode] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '', email: '' });
  const [services, setServices] = useState([]);
  const [topIds, setTopIds] = useState([]);        // C1: servicios más usados
  const [showAll, setShowAll] = useState(false);
  const [svcQuery, setSvcQuery] = useState('');
  const [serviceIds, setServiceIds] = useState([]); // LOTE 3: varios servicios por venta
  const serviceId = serviceIds[0] || '';
  const [concept, setConcept] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(null);
  const [paid, setPaid] = useState(false);
  const [lastSale, setLastSale] = useState(null);

  useEffect(() => { if (presetClient) setClient(presetClient); }, [presetClient]);

  useEffect(() => {
    api.get('/bookings/top-services?limit=4').then((r) => setTopIds((r?.items || []).map((x) => x.service_id))).catch(() => setTopIds([]));
    bookingApi.services(user.username || user.id).then((r) => setServices((r?.items || r || []).filter((s) => s.active !== false))).catch(() => setServices([]));
  }, [user.username, user.id]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => clientsApi.search(term).then((r) => setResults(r.items || [])).catch(() => setResults([])), 280);
    return () => clearTimeout(t);
  }, [q]);

  const selectedServices = useMemo(() => serviceIds.map((id) => services.find((s) => s.id === id)).filter(Boolean), [services, serviceIds]);
  const selectedService = selectedServices[0];
  useEffect(() => { if (!selectedServices.length && !concept.trim()) setPrice(''); if (selectedServices.length) setPrice(String(selectedServices.reduce((a, s) => a + Number(s.price || 0), 0).toFixed(2))); }, [selectedServices]);

  const total = Number(price || 0);
  const discountHint = client?.discount_pct ? Math.round(total * Number(client.discount_pct)) / 100 : 0;

  const [dupAsk, setDupAsk] = useState(null); // 21-oct-2026: aviso de nombre repetido
  const startSale = async (decision = {}) => {
    if (!(total >= 0) || price === '') { toast.error(tr("Indica el precio")); return; }
    if (!serviceId && !concept.trim()) { toast.error(tr("Elige un servicio o escribe el concepto")); return; }
    if (!client && newMode && newClient.name.trim() && !newClient.phone.trim() && !newClient.email.trim()) {
      toast.error(tr("Indica teléfono o correo del cliente (al menos uno), o cobra sin registrar cliente")); return;
    }
    setBusy(true);
    try {
      const body = { price: total, service_id: serviceId || undefined, service_ids: serviceIds.length > 1 ? serviceIds : undefined, service_name: serviceId ? undefined : concept.trim(), ...decision };
      if (client) body.client_id = client.id;
      else if (newMode) {
        if (newClient.name.trim()) body.client_name = newClient.name.trim();
        if (newClient.phone.trim()) body.client_phone = newClient.phone.trim();
        if (newClient.email.trim()) body.client_email = newClient.email.trim();
      }
      const r = await clientsApi.walkIn(body);
      setDupAsk(null);
      setBooking(r.booking);
    } catch (e) {
      if (isNameDuplicateError(e)) { setDupAsk({ duplicates: e.payload.detail.duplicates || [] }); return; }
      toast.error(e?.payload?.detail?.message || (typeof e?.payload?.detail === 'string' ? e.payload.detail : null) || e.message || tr("No pudimos iniciar la venta"));
    } finally { setBusy(false); }
  };

  const resetAll = () => {
    setBooking(null); setClient(null); setQ(''); setResults([]); setNewMode(false);
    setNewClient({ name: '', phone: '', email: '' }); setServiceIds([]); setConcept(''); setPrice('');
    onReset?.();
  };

  return (
    <div className="space-y-3 max-w-xl mx-auto" data-testid="walkin-sale-panel">
      <DuplicateAskDialog open={Boolean(dupAsk)} duplicates={dupAsk?.duplicates || []} busy={busy}
        onSame={(c) => startSale({ client_merge_into: c.id })} onDifferent={() => startSale({ client_force_new: true })} onCancel={() => setDupAsk(null)} />
      {lastSale && (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm flex items-center justify-between" data-testid="walkin-last-sale">
          <span>{tr("Venta registrada · {0} · {1}", lastSale.client_name, money(lastSale.total))}</span>
          <button type="button" onClick={() => setLastSale(null)} aria-label={tr("Cerrar")}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Cliente (opcional) */}
      <section className="bg-card border border-border rounded-2xl p-3 space-y-2" data-testid="walkin-client-block">
        <p className="text-xs font-semibold text-muted-foreground uppercase">{tr("Cliente")}{' '}<span className="normal-case font-normal">{tr("(opcional)")}</span></p>
        {client ? (
          <div className="flex items-center justify-between gap-2 bg-background border border-primary/40 rounded-xl px-3 py-2" data-testid="walkin-client-selected">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{client.full_name || client.display || client.name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{client.phone_display || client.phone || client.email || tr("Sin contacto")}{client.discount_pct ? tr("· descuento {0}%", client.discount_pct) : ''}</p>
            </div>
            <button type="button" onClick={() => setClient(null)} className="p-1.5 rounded-full hover:bg-secondary" aria-label={tr("Quitar cliente")} data-testid="walkin-client-clear"><X className="w-4 h-4" /></button>
          </div>
        ) : newMode ? (
          <div className="space-y-2" data-testid="walkin-new-client-form">
            <Input value={newClient.name} onChange={(e) => setNewClient((p) => ({ ...p, name: e.target.value }))} placeholder={tr("Nombre")} className="bg-background h-10" data-testid="walkin-new-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={newClient.phone} onChange={(e) => setNewClient((p) => ({ ...p, phone: e.target.value }))} placeholder={tr("Teléfono (o correo) *")} inputMode="tel" className="bg-background h-10" data-testid="walkin-new-phone" />
              <Input value={newClient.email} onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))} placeholder={tr("Correo (o teléfono) *")} inputMode="email" className="bg-background h-10" data-testid="walkin-new-email" />
            </div>
            <button type="button" onClick={() => setNewMode(false)} className="text-xs text-muted-foreground underline" data-testid="walkin-new-cancel">{tr("Buscar un cliente existente")}</button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Buscar por nombre o teléfono")} className="pl-9 bg-background h-10" data-testid="walkin-client-search" />
            </div>
            {results.length > 0 && (
              <ul className="divide-y divide-border rounded-xl border border-border overflow-hidden" data-testid="walkin-client-results">
                {results.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => { setClient(c); setQ(''); setResults([]); }} className="w-full text-left px-3 py-2 hover:bg-secondary/60 text-sm flex items-center justify-between" data-testid={`walkin-client-result-${c.id}`}>
                      <span className="truncate">{c.full_name || c.display || c.name}</span>
                      <span className="text-[11px] text-muted-foreground">{c.phone_display || c.phone || ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={() => setNewMode(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary" data-testid="walkin-new-client-btn">
              <UserPlus className="w-3.5 h-3.5" />{' '}{tr("Cliente nuevo o sin registrar")}</button>
          </div>
        )}
      </section>

      {/* Servicio / concepto */}
      <section className="bg-card border border-border rounded-2xl p-3 space-y-2" data-testid="walkin-service-block">
        <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-1"><Scissors className="w-3.5 h-3.5" />{' '}{tr("¿Qué vendes?")}</p>
        {services.length > 0 && (() => {
          const compact = services.length > 4 && !showAll && !svcQuery.trim();
          const q = svcQuery.trim().toLowerCase();
          let list = services;
          if (q) list = services.filter((s) => (s.name || '').toLowerCase().includes(q));
          else if (compact) {
            const top = topIds.map((id) => services.find((s) => s.id === id)).filter(Boolean);
            const padded = [...top, ...services.filter((s) => !top.includes(s))].slice(0, 4);
            const picked = services.filter((s) => serviceIds.includes(s.id) && !padded.includes(s));
            list = [...padded, ...picked];
          }
          const hidden = services.length - list.length;
          return (
            <>
              {services.length > 4 && (
                <Input value={svcQuery} onChange={(e) => setSvcQuery(e.target.value)} placeholder={tr("Buscar servicio…")} className="bg-background h-9 text-sm" data-testid="walkin-service-search" />
              )}
              <div className="flex flex-wrap gap-2" data-testid="walkin-service-chips">
                {list.map((s) => (
                  <button key={s.id} type="button" onClick={() => { setServiceIds((prev) => (prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])); setConcept(''); }}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${serviceIds.includes(s.id) ? 'bg-primary/15 border-primary text-primary' : 'border-border text-muted-foreground'}`} data-testid={`walkin-service-${s.id}`}>
                    {s.name} · {money(s.price)}
                  </button>
                ))}
                {q && list.length === 0 && <span className="text-xs text-muted-foreground">{tr("Sin resultados")}</span>}
              </div>
              {compact && hidden > 0 && (
                <button type="button" onClick={() => setShowAll(true)} className="text-xs font-semibold text-primary underline underline-offset-2" data-testid="walkin-show-all-services">
                  {tr("Ver todos los servicios")} ({services.length})
                </button>
              )}
              {!compact && showAll && services.length > 4 && !svcQuery.trim() && (
                <button type="button" onClick={() => setShowAll(false)} className="text-xs font-semibold text-primary underline underline-offset-2" data-testid="walkin-show-less-services">
                  {tr("Ver menos")}
                </button>
              )}
            </>
          );
        })()}
        <Input value={concept} onChange={(e) => { setConcept(e.target.value); if (e.target.value) setServiceIds([]); }} placeholder={tr("Monto manual: concepto opcional (ej. Pomada) y precio abajo")} className="bg-background h-10" maxLength={60} data-testid="walkin-concept-input" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{tr("Precio")}</span>
          <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" inputMode="decimal" className="bg-background h-10 w-32 text-right tabular-nums" data-testid="walkin-price-input" />
        </div>
        {discountHint > 0 && (
          <p className="text-[11px] text-amber-300 flex items-center gap-1" data-testid="walkin-discount-hint"><Percent className="w-3 h-3" />{' '}{tr("Este cliente tiene {0}% de descuento ≈ {1} — aplícalo en la pantalla de venta.", client.discount_pct, money(discountHint))}</p>
        )}
      </section>

      <Button onClick={() => startSale()} disabled={busy} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold text-base" data-testid="walkin-continue-btn">
        <Receipt className="w-4 h-4 mr-2" />{' '}{tr("Cobrar {0}", price !== '' ? money(total) : '')}</Button>
      <p className="text-[11px] text-muted-foreground text-center">{tr("Comisión vigente de Ziyup · QR con tarjeta o efectivo · recibo al correo si lo indicas.")}</p>

      {booking && (
        <SaleSheet booking={booking} open={Boolean(booking)}
          onOpenChange={async (o) => {
            if (o) return;
            if (!paid) { clientsApi.discardWalkIn(booking.id).catch(() => {}); setBooking(null); return; }
            try { const r = await bookingApi.getSale(booking.id); setLastSale({ client_name: booking.client_name, total: r?.sale?.total ?? booking.price }); }
            catch { setLastSale({ client_name: booking.client_name, total: booking.price }); }
            setPaid(false); resetAll();
          }}
          onPaid={() => setPaid(true)} />
      )}
    </div>
  );
};

export default WalkInSalePanel;
